import React, { useEffect, useState } from 'react';
import {
  Byline,
  ExhibitionContainer,
  Paragraph,
  SectionTitle,
  PageTitle,
  List,
  ListItem,
  HeroImage,
  FeatureImage,
  HeroCaption,
  FeatureCaption,
  MasonryGallery,
  MasonryItem,
  MasonryImage
} from '../../styles/exhibitionStyles';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

export default function ArtBaselMiami() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState('');

  const openLightbox = (imagePath) => {
    setCurrentImage(imagePath);
    setLightboxOpen(true);
  };

  // Masonry gallery images
  const masonryImages = [
    "/img/show-miami/miami-gallery-1.png",
    "/img/show-miami/miami-gallery-2.png",
    "/img/show-miami/miami-gallery-3.png",
    "/img/show-miami/miami-gallery-4.jpg",
    "/img/show-miami/miami-gallery-5.png",
    "/img/show-miami/miami-gallery-6.png",
    "/img/show-miami/miami-gallery-7.png",
    "/img/show-miami/miami-gallery-8.jpg",
    "/img/show-miami/miami-gallery-9.png",
    "/img/show-miami/miami-gallery-10.png",
    "/img/show-miami/miami-gallery-11.png",
    "/img/show-miami/miami-gallery-12.jpg",
  ];

  useEffect(() => {
    document.title = "Art Basel Miami 2023: Artist Residency - SLEEPR";
  }, []);

  return (
    <ExhibitionContainer>
      <div onClick={() => openLightbox("/img/show-miami/art-basel-feature.jpg")}>
        <HeroImage src="/img/show-miami/art-basel-feature.jpg" alt="Art Basel Miami Residency" />
      </div>
      <HeroCaption>Art Basel Miami Artist Residency - December 2023</HeroCaption>
      <PageTitle>Art Basel Miami 2023: Artist Residency</PageTitle>
      <Byline>In December 2023, Sleepr was one of the first to take up an artist residency at the world famous art fair Art Basel Miami, in an interactive performance piece spending 5 days creating a piece inside of a large opaque chamber.</Byline>

      <div onClick={() => openLightbox("/img/show-miami/miami-0.jpg")}>
        <FeatureImage src="/img/show-miami/miami-0.jpg" alt="The Opaque Chamber" />
      </div>

      <SectionTitle>The Artist Residency</SectionTitle>
      <Paragraph>
        The 2023 Art Basel Miami residency featured an extraordinary performance by the anonymous artist known only as Sleepr. This residency stood out among the many high-profile events at the fair, with its unique blend of conceptual depth and performative intensity. Sleepr's presence was marked by an enigmatic performance art piece that challenged traditional notions of accessibility and audience interaction, situating their work at the intersection of visual art and psychological exploration.
      </Paragraph>

      <div onClick={() => openLightbox("/img/show-miami/miami-1.png")}>
        <FeatureImage src="/img/show-miami/miami-1.png" alt="The Opaque Chamber" />
      </div>
      <div onClick={() => openLightbox("/img/show-miami/miami-2.png")}>
        <FeatureImage src="/img/show-miami/miami-2.png" alt="The Opaque Chamber" />
      </div>

      <SectionTitle>An Opaque Chamber: Hidden Performance Art</SectionTitle>
      <Paragraph>
        During the five-day residency, Sleepr was entirely hidden from view, confined within an opaque chamber. This physical separation between the artist and the public was central to the conceptual framework of the performance. The artist, concealed behind a Venetian mask, was only reachable through a singular point of contact—a red phone connecting the public directly to Sleepr's thoughts. This setup created a highly controlled environment where interaction was mediated not by sight, but by voice and imagination.
      </Paragraph>
      <div onClick={() => openLightbox("/img/show-miami/miami-3.jpg")}>
        <FeatureImage src="/img/show-miami/miami-3.jpg" alt="The Opaque Chamber" />
      </div>
      

      <SectionTitle>The Public Engagement: Over 500 Conversations</SectionTitle>
      <Paragraph>
        Over the course of the residency, more than 500 conversations took place between Sleepr and the public. These interactions ranged from the deeply personal to the abstract, covering a spectrum of human emotions and experiences. Each conversation contributed to the creation of a single artwork, "Medicine For The Soul," a piece that encapsulates the residency's themes of connection, confession, and catharsis. The artwork is not just a visual product, but a manifestation of the collective experiences shared during the residency.
      </Paragraph>

      <MasonryGallery>
        {masonryImages.map((image, index) => (
          <MasonryItem key={index} onClick={() => openLightbox(image)}>
            <MasonryImage src={image} alt={`Exhibition view ${index + 1}`} />
          </MasonryItem>
        ))}
      </MasonryGallery>

      <div onClick={() => openLightbox("/img/show-miami/miami-4.jpg")}>
        <FeatureImage src="/img/show-miami/miami-4.jpg" alt="Public Interaction" />
      </div>

      <SectionTitle>Anonymity and Authenticity</SectionTitle>
      <Paragraph>
        Sleepr explored the nuanced interplay between authenticity and anonymity through a unique artistic performance piece. By enclosing themselves inside an opaque chamber for five days and maintaining anonymity while actively engaging with the public, Sleepr challenges conventional notions of identity and presence in the art world.
      </Paragraph>
      <Paragraph>
        This juxtaposition not only invites contemplation on the essence of genuine artistic expression but also fosters a dynamic dialogue between the artist's hidden persona and their immediate, tangible interaction with audiences. The residency serves as a compelling experiment in how anonymity can both obscure and amplify the impact of an artist's work.
      </Paragraph>

      <div onClick={() => openLightbox("/img/artworks-1920/sleepr_1920_099.jpg")}>
        <FeatureImage src="/img/artworks-1920/sleepr_1920_099.jpg" alt="Public Interaction" />
      </div>
      <SectionTitle>Medicine For The Soul</SectionTitle>
      <Paragraph>
      The artwork, "Medicine For The Soul," created in real-time during over 500 conversations with the general public, and is a testament to the complex interplay of ideas and emotions that characterized the residency. Sleepr described the work as a "dense hypernetwork of conflicting and interwoven ideas with these sharp glistening breakthroughs of clarity and perspective," capturing the essence of the dialogue-driven creative process. The piece is as much about the process of creation as it is about the final visual outcome, reflecting the intensity of the artist's experience within the chamber.
      </Paragraph>

      <List>
        <ListItem>The first work made by an artist in resident at Art Basel Miami</ListItem>
        <ListItem>The first work made by an anonymous artist at a major international art fair</ListItem>
        <ListItem>The first major art fair the Solana blockchain has participated in</ListItem>
      </List>

      <Paragraph>The work was sold at auction on the final day of the fair for approximately $10,000. </Paragraph>

      <div onClick={() => openLightbox("/img/show-miami/miami-5.jpg")}>
        <FeatureImage src="/img/show-miami/miami-5.jpg" alt="Installation Detail" />
      </div>
      <FeatureCaption>Sleepr was refused entry on multiple occasions into backstage areas at Art Basel Miami for presenting a useless anonymous security pass when asked for ID.</FeatureCaption>

      <SectionTitle>In Between Two Worlds</SectionTitle>
      <Paragraph>
      Sleepr's performance at Art Basel Miami signals an exciting merging of two seemingly distinct realms: the technologically driven blockchain community and the traditional, institution-based art world. Through the artist's bold, concealed-yet-interactive residency, Sleepr has highlighted the vibrant liminal spaces where these two artistic titans intersect. It is in these overlooked corridors—those nooks and crannies—where the most innovative art often blossoms, for they are truly the ones fertilised by the reality and context of the world we live in - which may very well just be perhaps not the one we thought we were in. In this shifting landscape of new technologies, anonymity, and radical expression, the lines that once demarcated reality, virtuality, and imagination blur, hinting that the most significant artistic breakthroughs may emerge precisely where we least expect them.
      </Paragraph>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={[{ src: currentImage }]}
        carousel={{ finite: true }}
        render={{ 
          buttonPrev: () => null,
          buttonNext: () => null
        }}
      />
    </ExhibitionContainer>
  );
}
